<script setup lang="ts">
/**
 * Content of the notification bell's panel: the kit's NxNotificationPanel
 * (head, NxFeedItem rows, states, paging) bound to master's notifications.
 * The bell itself is a NavBarItem in ShellNav; the NavBar renders this
 * component in its panel while it is open and owns opening, closing and the
 * frame.
 *
 * A row opens what the notification links to (`notificationLinks.ts`). The
 * notification's own actions (`notificationActions.ts`) stand always visible
 * under its text; „Später erinnern“ and „Als gelesen markieren“ show next to
 * it on hover and focus. A resolved one says how, as a badge after its body
 * (I-2915).
 */
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import ArrowRight from '@lucide/vue/dist/esm/icons/arrow-right.mjs'; import Check from '@lucide/vue/dist/esm/icons/check.mjs';
import {
  NxBadge,
  NxButton,
  NxIconButton,
  NxNotificationPanel,
  useNxToasts,
  type NxFeedEntry,
} from "@blpde/nx-ui";
import NotificationSnoozeMenu from "@/components/NotificationSnoozeMenu.vue";
import {
  notificationActionsOf,
  notificationActionVariant,
  useNotificationActions,
} from "@/notificationActions";
import { openNotificationLink } from "@/notificationLinks";
import {
  useMasterNotifications,
  type MasterNotification,
  type NotificationAction,
} from "@/notifications";
import { useNotificationText } from "@/notificationText";

const emit = defineEmits<{
  /** Closes the panel: the NavBar returns focus to the bell. */
  close: [];
}>();

const { t } = useI18n();
const router = useRouter();
const { showToast } = useNxToasts();
const { titleOf, bodyOf, moduleLabel, actionLabel, resolvedActionLabel } =
  useNotificationText();
const actions = useNotificationActions();
// Mounted with the open panel, so every opening loads the list afresh.
const notifications = useMasterNotifications();
const panel = useTemplateRef<InstanceType<typeof NxNotificationPanel>>("panel");

// Unread notifications only, all of them; a read one leaves the list.
const entries = computed<NxFeedEntry[]>(() =>
  notifications.items.value.map((n) => ({
    id: n.id,
    title: titleOf(n),
    excerpt: bodyOf(n) || undefined,
    time: n.notifyAt || n.createdAt || n.updatedAt,
    unread: !n.readAt,
    source: moduleLabel(n.moduleKey) || undefined,
    count: n.groupCount,
  })),
);

function notificationOf(entry: NxFeedEntry): MasterNotification | undefined {
  return notifications.items.value.find((n) => n.id === entry.id);
}

function markAllRead() {
  // The button goes with the last unread notification: keep focus in the
  // panel, or Escape and Tab would start from the page.
  panel.value?.focus();
  void notifications.markAllRead();
}

/** Marks one read without opening it; its row leaves the list, and the panel
 * hands the focus to the row in its place. */
function markRead(entry: NxFeedEntry) {
  void notifications.markRead(Number(entry.id));
}

async function openNotification(entry: NxFeedEntry) {
  const n = notificationOf(entry);
  emit("close");
  if (!n) return;
  // Mark read only after successful navigation so dead links keep unread state.
  const ok = await openNotificationLink(router, n.link, {
    companyID: n.companyID,
  });
  if (ok && !n.readAt) void notifications.markRead(n.id);
}

/** How a row was dealt with („Erledigt: Freigegeben“); empty while it is not. */
function resolvedOf(entry: NxFeedEntry): string {
  const n = notificationOf(entry);
  const done = n ? resolvedActionLabel(n) : "";
  return done ? t("connect.notifications.resolvedWith", { action: done }) : "";
}

/** The actions of a row; none once the notification is dealt with. */
function actionsOf(entry: NxFeedEntry): NotificationAction[] {
  const n = notificationOf(entry);
  return n ? notificationActionsOf(n) : [];
}

/** The key of the action a row is performing: its button spins, the others rest. */
function runningOf(entry: NxFeedEntry): string | undefined {
  const n = notificationOf(entry);
  return n ? actions.runningAction(n) : undefined;
}

/**
 * Performs an action. Done, the notification is resolved and read: its row
 * leaves the list. Failed, it stays as it is (the error is shown).
 */
function runAction(entry: NxFeedEntry, action: NotificationAction) {
  const n = notificationOf(entry);
  if (!n) return;
  // An action that opens something leads away from the panel — its own link,
  // or the notification itself when the request belongs to another company.
  if (!action.request) emit("close");
  void actions.run(n, action).then((result) => {
    if (result.redirected) emit("close");
  });
}

/** „Später erinnern“: the row leaves the list until then. */
async function snooze(entry: NxFeedEntry, until: Date) {
  const ok = await notifications.snooze(Number(entry.id), until);
  if (ok) showToast(t("connect.notifications.snoozed"));
  else showToast(t("connect.notifications.actionFailed"), "error");
}

function openAll() {
  emit("close");
  void router.push("/notifications");
}
</script>

<template>
  <div class="cx-notif-panel">
    <NxNotificationPanel
      ref="panel"
      :items="entries"
      :unread-count="notifications.unreadCount.value"
      :loading="notifications.loading.value && !notifications.loaded.value"
      :error="
        notifications.error.value ? t('connect.notifications.error') : undefined
      "
      :has-more="notifications.hasMore.value"
      :label="t('connect.notifications.title')"
      :mark-all-label="t('connect.notifications.markAllRead')"
      :loading-label="t('connect.notifications.loading')"
      :retry-label="t('connect.notifications.retry')"
      :empty-title="t('connect.notifications.empty')"
      :empty-hint="t('connect.notifications.emptyHint')"
      :load-more-label="t('connect.notifications.loadMore')"
      :load-more-error-label="t('connect.notifications.error')"
      :item-unread-label="t('connect.notifications.unreadLabel')"
      @load-more="notifications.loadMore"
      @open="openNotification"
      @mark-all="markAllRead"
      @retry="notifications.refresh()"
    >
      <template #item-excerpt="{ item }">
        {{ item.excerpt ? `${item.excerpt} ` : "" }}
        <NxBadge
          v-if="resolvedOf(item)"
          variant="success"
          :icon="Check"
          data-testid="notification-resolved"
        >
          {{ resolvedOf(item) }}
        </NxBadge>
      </template>
      <template #item-primary-actions="{ item }">
        <NxButton
          v-for="action in actionsOf(item)"
          :key="action.key"
          :variant="notificationActionVariant(action)"
          :loading="runningOf(item) === action.key"
          :disabled="!!runningOf(item) && runningOf(item) !== action.key"
          :data-testid="`notification-action-${action.key}`"
          @click="runAction(item, action)"
        >
          {{ actionLabel(notificationOf(item)!, action) }}
        </NxButton>
      </template>
      <template #item-actions="{ item }">
        <NotificationSnoozeMenu @snooze="(until) => snooze(item, until)" />
        <NxIconButton
          variant="ghost"
          :aria-label="t('connect.notifications.markRead')"
          :title="t('connect.notifications.markRead')"
          @click="markRead(item)"
        >
          <Check class="nxt-icon-sm" />
        </NxIconButton>
      </template>
      <template #footer>
        <NxButton variant="ghost" @click="openAll">
          {{ t("connect.notifications.openAll") }}
          <ArrowRight class="nxt-icon-sm" aria-hidden="true" />
        </NxButton>
      </template>
    </NxNotificationPanel>
  </div>
</template>

<style scoped>
.cx-notif-panel {
  /* The NavBar's panel frames and bounds; this sets the width. */
  width: var(--nxt-panel-width-2xl);
  max-width: 100%;
  box-sizing: border-box;
  padding: var(--nxt-space-xs);
}
</style>
